import { useLazyQuery, useMutation } from "@apollo/client";
import useCoreStore from "views/hooks/useCoreStore";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientNeoCurrencyExchange } from "@neonumy/api-clients";
import { useEffect, useMemo } from "react";
import {
  CREATE_CURRENCY,
  DISABLE_CURRENCY,
  ENABLE_CURRENCY,
  GET_CURRENCY_SETTING,
  LIST_CURRENCIES_OPTIONS,
  LIST_CURRENCIES_TABLE,
  UPDATE_CURRENCY,
} from "queries/neoFact/currency";
export interface CurrencyOptionTypes {
  label: string | React.ReactNode;
  value: ID;
  key: ID;
  isMainCurrency: boolean;
  decimalPlaces: number;
}
export default function useCurrencyApi() {
  const setCurrencyOptions = useCoreStore(state => state.setCurrencyOptions);

  const [
    listCurrencyTableData,
    {
      refetch: listCurrenciesRefetch,
      loading: listCurrenciesLoading,
      data: listCurrencyTableQueryData,
      error: listCurrencyTableError,
    },
  ] = useLazyQuery<ListCurrenciesPaginatedOutput, ListCurrenciesPaginatedInput>(
    LIST_CURRENCIES_TABLE,
    {
      client: clientNeoCurrencyExchange,
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    if (listCurrencyTableError) onErrorGraphql(listCurrencyTableError);
  }, [listCurrencyTableError]);

  const currenciesData: ListCurrenciesPaginatedData[] | undefined =
    listCurrencyTableQueryData?.listCurrencySettings?.results?.map((item: any) => ({
      ...item,
      key: item.id,
    }));

  const totalCount = listCurrencyTableQueryData?.listCurrencySettings?.totalCount ?? 0;

  const [
    listCurrenciesOptions,
    {
      refetch: listCurrenciesOptionsRefetch,
      loading: listCurrenciesOptionsLoading,
      data: listCurrenciesOptionsData,
      error: listCurrenciesOptionsError,
    },
  ] = useLazyQuery<ListCurrenciesPaginatedOutput, ListCurrenciesPaginatedInput>(
    LIST_CURRENCIES_OPTIONS,
    {
      client: clientNeoCurrencyExchange,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (listCurrenciesOptionsError) onErrorGraphql(listCurrenciesOptionsError);
  }, [listCurrenciesOptionsError]);

  const currenciesOption: CurrencyOptionTypes[] = useMemo(
    () =>
      listCurrenciesOptionsData?.listCurrencySettings?.results?.map((item: any) => ({
        ...item,
        key: item.id,
        label: `${item.currency?.name} (${item.currency?.currency})`,
        value: item.code,
      })) ?? [],
    [listCurrenciesOptionsData],
  );

  useEffect(() => {
    if (currenciesOption.length > 0) {
      setCurrencyOptions(currenciesOption as unknown as Parameters<typeof setCurrencyOptions>[0]);
    }
  }, [currenciesOption]);

  const [createCurrency] = useMutation<CreateCurrencyOutput, CreateCurrencyInput>(CREATE_CURRENCY, {
    client: clientNeoCurrencyExchange,
    fetchPolicy: "no-cache",
  });

  const [updateCurrencySetting] = useMutation<UpdateCurrencyOutput, UpdateCurrencyInput>(
    UPDATE_CURRENCY,
    {
      client: clientNeoCurrencyExchange,
      fetchPolicy: "no-cache",
    },
  );

  const [disableCurrencySetting] = useMutation<DisableCurrencyResponse, DisableCurrencyVars>(
    DISABLE_CURRENCY,
    {
      client: clientNeoCurrencyExchange,
      fetchPolicy: "no-cache",
    },
  );

  const [enableCurrencySetting] = useMutation<EnableCurrencyResponse, EnableCurrencyVars>(
    ENABLE_CURRENCY,
    {
      client: clientNeoCurrencyExchange,
      fetchPolicy: "no-cache",
    },
  );

  const [
    getCurrencySetting,
    { refetch: refetchGetCurrency, data: getCurrencySettingData, error: getCurrencySettingError },
  ] = useLazyQuery<GetCurrencyOutput, GetVarTypeID>(GET_CURRENCY_SETTING, {
    client: clientNeoCurrencyExchange,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getCurrencySettingError) onErrorGraphql(getCurrencySettingError);
  }, [getCurrencySettingError]);

  const currencyFormData = getCurrencySettingData?.getCurrencySetting;

  return {
    totalCount,
    getCurrencySetting,
    listCurrencyTableData,
    createCurrency,
    currenciesData,
    currencyFormData,
    refetchGetCurrency,
    listCurrenciesOptionsRefetch,
    listCurrenciesOptionsLoading,
    listCurrenciesLoading,
    currenciesOption,
    listCurrenciesRefetch,
    listCurrenciesOptions,
    updateCurrencySetting,
    enableCurrencySetting,
    disableCurrencySetting,
  };
}
